import React, { useState, useEffect, useCallback } from "react";
import styles from "./IntervendQueueScreen.module.scss";
import classNames from "classnames";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import DashboardLayout from "../../../components/layout/DashboardLayout";
import ResourceList from "../../../components/resourceList/ResourceList";
import AreYouSureModal from "../../../components/modals/areYouSureModal/AreYouSureModal";
import BaseModal from "../../../components/modals/baseModal/BaseModal";
import SelectItem from "../../../components/selectItem/SelectItem";
import Button from "../../../components/button/Button";
import moment from "moment";
import { isMobile } from "react-device-detect";
import {
  fetchQueueMetrics,
  fetchQueueSummary,
  fetchQueueJobs,
  truncateQueue,
  truncateQueueResetState,
} from "../../../store/actions/admin/intervendQueue";

const HEADER_VALUES = [
  { label: "JOB ID", sortable: false },
  { label: "TIP", sortable: false },
  { label: "KREIRANO", sortable: false },
  { label: "POKUŠAJI", sortable: false },
  { label: "STATUS", sortable: false },
  { label: "AKCIJE", sortable: false },
];

const STATE_OPTIONS = [
  { value: "waiting", label: "Waiting" },
  { value: "active", label: "Active" },
  { value: "delayed", label: "Delayed" },
  { value: "failed", label: "Failed" },
  { value: "completed", label: "Completed" },
];

const TRUNCATE_OPTIONS = [
  { value: "all", label: "All Jobs" },
  { value: "waiting", label: "Waiting Jobs" },
  { value: "active", label: "Active Jobs" },
  { value: "delayed", label: "Delayed Jobs" },
  { value: "failed", label: "Failed Jobs" },
  { value: "completed", label: "Completed Jobs" },
];

const IntervendQueueScreen = () => {
  const dispatch = useDispatch();
  const [selectedState, setSelectedState] = useState("waiting");
  const [truncateModalOpen, setTruncateModalOpen] = useState(false);
  const [truncateState, setTruncateState] = useState("all");
  const [jobDetailsModalOpen, setJobDetailsModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const metrics = useSelector((state) => state.intervendQueue.metrics);
  const summary = useSelector((state) => state.intervendQueue.summary);
  const jobs = useSelector((state) => state.intervendQueue.jobs);
  const truncateInfo = useSelector((state) => state.intervendQueue.truncate);

  const loadData = useCallback(() => {
    dispatch(fetchQueueMetrics());
    dispatch(fetchQueueSummary());
    dispatch(fetchQueueJobs(selectedState, 1, 20));
  }, [dispatch, selectedState]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      loadData();
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, loadData]);

  useEffect(() => {
    if (truncateInfo.success) {
      toast.success(`Successfully removed ${truncateInfo.data?.removed || 0} jobs`);
      setTruncateModalOpen(false);
      dispatch(truncateQueueResetState());
      loadData();
    }
  }, [truncateInfo.success, dispatch, loadData]);

  useEffect(() => {
    if (truncateInfo.error) {
      toast.error("Error truncating queue");
      dispatch(truncateQueueResetState());
    }
  }, [truncateInfo.error, dispatch]);

  const onLoadPage = (page) => {
    dispatch(fetchQueueJobs(selectedState, page, jobs.data.perPage));
  };

  const handleStateChange = (state) => {
    setSelectedState(state);
    dispatch(fetchQueueJobs(state, 1, 20));
  };

  const handleTruncateConfirm = () => {
    dispatch(truncateQueue(truncateState));
  };

  const handleViewDetails = (job) => {
    setSelectedJob(job);
    setJobDetailsModalOpen(true);
  };

  const getStatusColor = () => {
    if (!summary.data) return "green";
    if (summary.data.hasFailures) return "red";
    if (summary.data.hasBacklog) return "yellow";
    return "green";
  };

  const renderMetricCard = (title, value, icon) => {
    return (
      <div className={styles.metricCard}>
        <div className={styles.metricIcon}>{icon}</div>
        <div className={styles.metricContent}>
          <p className={styles.metricValue}>{value ?? "-"}</p>
          <p className={styles.metricTitle}>{title}</p>
        </div>
      </div>
    );
  };

  const renderJobRow = (job, index) => {
    const createdAt = moment(job.timestamp).format("DD-MM-YYYY HH:mm:ss");
    const statusColor = selectedState === "failed" ? "red" : selectedState === "active" ? "blue" : "green";

    if (isMobile) {
      return (
        <div className={styles.listCard} key={job.id}>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Job ID</p>
            <p className={styles.rowCardValue}>{job.id}</p>
          </div>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Tip</p>
            <p className={styles.rowCardValue}>{job.name}</p>
          </div>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Kreirano</p>
            <p className={styles.rowCardValue}>{createdAt}</p>
          </div>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Pokušaji</p>
            <p className={styles.rowCardValue}>{job.attemptsMade}/{job.opts?.attempts || 3}</p>
          </div>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Status</p>
            <span className={classNames(styles.statusBadge, styles[statusColor])}>
              {selectedState}
            </span>
          </div>
          <div className={styles.rowCard}>
            <button
              className={styles.viewButton}
              onClick={() => handleViewDetails(job)}
            >
              View Details
            </button>
          </div>
        </div>
      );
    }

    return (
      <div
        className={classNames(
          styles.row,
          index % 2 === 0 ? styles.even : styles.odd
        )}
        key={job.id}
      >
        <div className={styles.cell}>
          <p className={styles.jobId}>{job.id}</p>
        </div>
        <div className={styles.cell}>
          <p>{job.name}</p>
        </div>
        <div className={styles.cell}>
          <p>{createdAt}</p>
        </div>
        <div className={styles.cell}>
          <p>{job.attemptsMade}/{job.opts?.attempts || 3}</p>
        </div>
        <div className={styles.cell}>
          <span className={classNames(styles.statusBadge, styles[statusColor])}>
            {selectedState}
          </span>
        </div>
        <div className={styles.cell}>
          <button
            className={styles.viewButton}
            onClick={() => handleViewDetails(job)}
          >
            View Details
          </button>
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout>
      <AreYouSureModal
        modalOpen={truncateModalOpen}
        closeModal={() => setTruncateModalOpen(false)}
        onConfirm={handleTruncateConfirm}
        title="Truncate Queue"
        message={
          <div>
            <p>Are you sure you want to remove jobs?</p>
            <p><strong>State:</strong> {truncateState}</p>
            <p className={styles.warningText}>
              <strong>Warning:</strong> This action cannot be undone!
            </p>
          </div>
        }
      />

      <BaseModal
        modalOpen={jobDetailsModalOpen}
        onClose={() => {
          setJobDetailsModalOpen(false);
          setSelectedJob(null);
        }}
        title="Job Details"
      >
        {selectedJob && (
          <div className={styles.jobDetails}>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Job ID:</span>
              <span className={styles.detailValue}>{selectedJob.id}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Type:</span>
              <span className={styles.detailValue}>{selectedJob.name}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Created:</span>
              <span className={styles.detailValue}>
                {moment(selectedJob.timestamp).format("DD-MM-YYYY HH:mm:ss")}
              </span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Attempts:</span>
              <span className={styles.detailValue}>
                {selectedJob.attemptsMade}/{selectedJob.opts?.attempts || 3}
              </span>
            </div>
            {selectedJob.failedReason && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Error:</span>
                <span className={classNames(styles.detailValue, styles.error)}>
                  {selectedJob.failedReason}
                </span>
              </div>
            )}
            {selectedJob.processedOn && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Processed On:</span>
                <span className={styles.detailValue}>
                  {moment(selectedJob.processedOn).format("DD-MM-YYYY HH:mm:ss")}
                </span>
              </div>
            )}
            {selectedJob.finishedOn && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Finished On:</span>
                <span className={styles.detailValue}>
                  {moment(selectedJob.finishedOn).format("DD-MM-YYYY HH:mm:ss")}
                </span>
              </div>
            )}
            <div className={styles.detailSection}>
              <span className={styles.detailLabel}>Job Data:</span>
              <pre className={styles.jsonData}>
                {JSON.stringify(selectedJob.data, null, 2)}
              </pre>
            </div>
            <div className={styles.detailSection}>
              <span className={styles.detailLabel}>Job Options:</span>
              <pre className={styles.jsonData}>
                {JSON.stringify(selectedJob.opts, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </BaseModal>

      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>Intervend Sync Queue</h1>
          <div className={styles.headerActions}>
            <label className={styles.autoRefreshLabel}>
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className={styles.checkbox}
              />
              Auto-refresh (30s)
            </label>
            <button className={styles.refreshButton} onClick={loadData}>
              ↻ Refresh
            </button>
          </div>
        </div>

        <div className={styles.statusIndicator}>
          <div className={classNames(styles.statusDot, styles[getStatusColor()])} />
          <span className={styles.statusText}>
            {summary.data?.hasFailures ? "Has Failures" : 
             summary.data?.hasBacklog ? "Has Backlog" : "All Clear"}
          </span>
        </div>

        <div className={styles.metricsGrid}>
          {renderMetricCard("Active", metrics.data?.active, "⚡")}
          {renderMetricCard("Waiting", metrics.data?.waiting, "⏳")}
          {renderMetricCard("Delayed", metrics.data?.delayed, "⏰")}
          {renderMetricCard("Failed", metrics.data?.failed, "❌")}
          {renderMetricCard("Completed", metrics.data?.completed, "✓")}
        </div>

        <div className={classNames(styles.section, styles.marginTop)}>
          <div className={styles.sectionHeader}>
            <div className={styles.filterWrapper}>
              <label className={styles.filterLabel}>Filter by State:</label>
              <SelectItem
                options={STATE_OPTIONS}
                value={selectedState}
                onChange={handleStateChange}
              />
            </div>
            <Button
              name="Truncate Queue"
              onClick={() => setTruncateModalOpen(true)}
            />
          </div>

          {truncateModalOpen && (
            <div className={styles.truncateOptions}>
              <label className={styles.filterLabel}>Select State to Clear:</label>
              <SelectItem
                options={TRUNCATE_OPTIONS}
                value={truncateState}
                onChange={setTruncateState}
              />
            </div>
          )}

          <ResourceList
            resource={jobs.data}
            onSort={() => {}}
            onLoadPage={onLoadPage}
            headerValues={HEADER_VALUES}
            rowRender={renderJobRow}
            showLoader={true}
          />
        </div>
      </div>
    </DashboardLayout>
  );
};

export default IntervendQueueScreen;

